import type { Order, OrderItem } from '@/lib/vision/schemas'

/**
 * Ordered / sent-to-warehouse / remaining for one PO list row.
 *
 * Prefer Vision's PO-level paise fields when present so the queue and the
 * workstream-3 summary strip share one sum. Fall back to the same derivation
 * over `items` for older Vision builds.
 *
 * Sent-to-warehouse is `fulfilledQuantity` (OMS waves), never the ambiguous
 * legacy `shippedQty` alias. B2B actual-shipped values live on Sales Order
 * document source lines and do not participate in this calculation.
 * Remaining is accepted leftover when the line is Accepted, zero when Rejected,
 * otherwise ordered leftover. The Sent UI treats 0 as `—`.
 */
export interface PoListFulfilment {
  orderedQty: number
  orderedValueRupees: number
  sentQty: number | null
  sentValueRupees: number | null
  remainingQty: number
  remainingValueRupees: number
}

export function paiseToRupees(paise: number | null | undefined): number {
  return (paise ?? 0) / 100
}

export function poListFulfilment(order: Order): PoListFulfilment {
  if (order.orderedQty != null && order.orderedValuePaise != null) {
    const derived = fromItems(order)
    const sentQty = order.sentToWarehouseQty ?? derived.sentQty ?? 0
    const sentPaise = order.sentToWarehouseValuePaise ?? (derived.sentValueRupees ?? 0) * 100
    const remainingQty = order.remainingQty ?? order.pendingQty ?? 0
    const remainingPaise = order.remainingValuePaise ?? order.pendingValuePaise ?? 0
    return withEmptySent({
      orderedQty: order.orderedQty,
      orderedValueRupees: paiseToRupees(order.orderedValuePaise),
      sentQty,
      sentValueRupees: paiseToRupees(sentPaise),
      remainingQty,
      remainingValueRupees: paiseToRupees(remainingPaise),
    })
  }
  return withEmptySent(fromItems(order))
}

function fromItems(order: Order): PoListFulfilment {
  let orderedQty = 0
  let orderedValue = 0
  let sentQty = 0
  let sentValue = 0
  let remainingQty = 0
  let remainingValue = 0
  for (const item of order.items) {
    const ordered = Number(item.orderedQuantity ?? 0)
    const unit = item.netAmount?.amount != null ? Number(item.netAmount.amount) : 0
    const lineValue = unit * ordered
    const fulfilled = Number(item.fulfilledQuantity ?? 0)
    const remaining = Math.max(0, lineOwed(item) - fulfilled)
    orderedQty += ordered
    orderedValue += lineValue
    sentQty += fulfilled
    sentValue += share(lineValue, fulfilled, ordered)
    remainingQty += remaining
    remainingValue += share(lineValue, remaining, ordered)
  }
  return {
    orderedQty,
    orderedValueRupees: orderedValue,
    sentQty,
    sentValueRupees: sentValue,
    remainingQty,
    remainingValueRupees: remainingValue,
  }
}

function lineOwed(item: OrderItem): number {
  if (item.ackCode === 'Rejected') return 0
  if (item.ackCode === 'Accepted') return Number(item.ackQuantity ?? 0)
  return Number(item.orderedQuantity ?? 0)
}

function share(lineValue: number, part: number, whole: number): number {
  if (lineValue === 0 || whole === 0) return 0
  return (lineValue * part) / whole
}

function withEmptySent(totals: PoListFulfilment): PoListFulfilment {
  if (totals.sentQty != null && totals.sentQty > 0) return totals
  return { ...totals, sentQty: null, sentValueRupees: null }
}
